import {
  GroupExecution,
  PipelineTasks,
  TableGroup,
  TASK_KINDS,
  TaskKind,
} from './types';
import { taskVarNames } from './paths';

/**
 * 실행 순서.
 *
 * 이전 모델은 task 마다 `step` 을 받아 같은 step 을 병렬로 묶었고, 그 로직이
 * 세 곳에 독립 구현되어 있었다. 새 규약에서는 **그룹 안의 순서가 도메인으로
 * 정해진다** — 테이블을 만들고(`create`), 그 구간을 지우고(`delete`), 다시
 * 넣는다(`insert`). 순서를 바꿀 수 있게 하면 "지우기가 넣기 뒤에 와서 방금
 * 넣은 걸 지운다" 같은 조용한 실패가 가능해진다.
 *
 * 사용자가 고르는 것은 **그룹 사이** 순서뿐이다 — A 테이블을 채운 뒤 B 를
 * 채울지(sequential), 둘을 동시에 돌릴지(parallel).
 */
export function enabledTaskKinds(tasks: PipelineTasks): TaskKind[] {
  return TASK_KINDS.filter(kind => tasks[kind] !== undefined);
}

/** 그룹 하나의 operator 변수 이름들, `create → delete → insert` 순서. */
export function groupOperatorNames(group: TableGroup): string[] {
  return enabledTaskKinds(group.tasks).map(
    kind => taskVarNames(kind, group.table_id).operator,
  );
}

/**
 * `.py` 의 체인 문장들. 각 원소가 한 줄이다.
 *
 * - sequential: 그룹을 한 줄로 이어 붙인다
 * - parallel: 그룹마다 `start → … → finish` 한 줄씩 — Airflow 가 START 에서
 *   갈라져 END 에서 합친다
 *
 * task 가 하나도 없는 그룹은 건너뛴다(저장은 스키마가 막지만, 폼 중간 상태의
 * 프리뷰가 이 함수를 쓴다).
 */
export function renderChainStatements(
  groups: TableGroup[],
  execution: GroupExecution,
): string[] {
  return renderChainOperands(groups, execution).map(ops => ops.join(' >> '));
}

/** 체인 문장마다 `>>` 로 이을 피연산자들. `.py` 렌더가 줄 접기를 직접 하려고 쓴다. */
export function renderChainOperands(
  groups: TableGroup[],
  execution: GroupExecution,
): string[][] {
  const chains = groups.map(groupOperatorNames).filter(ops => ops.length > 0);
  if (chains.length === 0) return [['start_pipeline', 'finish_pipeline']];
  if (execution === 'sequential') {
    return [['start_pipeline', ...chains.flat(), 'finish_pipeline']];
  }
  return chains.map(ops => ['start_pipeline', ...ops, 'finish_pipeline']);
}

/** 사람이 읽는 라벨. UI 프리뷰용. */
export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  create: '테이블 생성',
  delete: '기존 데이터 지우기',
  insert: '새로 넣기',
};

/** UI 프리뷰. 중간 상태(이름 없는 테이블, task 0 개)도 표시할 수 있다. */
export function renderChainPreview(
  groups: TableGroup[],
  execution: GroupExecution,
): string {
  const parts = groups.map(group => {
    const label = group.table_id.trim() || '(테이블 이름 없음)';
    const kinds = enabledTaskKinds(group.tasks).map(k => TASK_KIND_LABELS[k]);
    return kinds.length ? `${label}: ${kinds.join(' → ')}` : `${label}: (없음)`;
  });
  if (!parts.length) return 'START → END';
  if (execution === 'sequential') {
    return `START → ${parts.join(' → ')} → END`;
  }
  return parts.map(p => `START → ${p} → END`).join('\n');
}
